import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, onAuthStateChanged } from 'firebase/auth'
import { auth, authService } from '@/lib/firebase'
import { userProfileService } from '@/services/userProfileService'
import { UserProfile } from '@/types'

interface AuthContextType {
  // Authentication state
  currentUser: User | null
  loading: boolean
  
  // User profile state
  userProfile: UserProfile | null
  profileLoading: boolean
  
  // User info helpers
  isAuthenticated: boolean
  isAdmin: boolean
  userEmail: string
  displayName: string
  companyName: string
  
  // Authentication functions
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: string | null }>
  signUp: (email: string, password: string) => Promise<{ user: User | null; error: string | null }>
  signInWithGoogle: () => Promise<{ user: User | null; error: string | null }>
  signOut: () => Promise<{ error: string | null }>
  resetPassword: (email: string) => Promise<{ error: string | null }>
  
  // Profile functions
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)

  const loadUserProfile = async (user: User) => {
    setProfileLoading(true)
    try {
      const profile = await userProfileService.getUserProfile(user.uid)

      if (profile) {
        setUserProfile(profile)
      } else {
        await userProfileService.createUserProfile(user)
        const newProfile = await userProfileService.getUserProfile(user.uid)
        setUserProfile(newProfile)
      }
    } catch (error) {
      console.error('Error loading user profile:', error)
    } finally {
      setProfileLoading(false)
    }
  }

  const refreshProfile = async () => {
    if (currentUser) {
      await loadUserProfile(currentUser)
    }
  }
  const signIn = async (email: string, password: string) => {
    return await authService.signInWithEmail(email, password)
  }

  const signUp = async (email: string, password: string) => {
    const result = await authService.createAccount(email, password)

    if (result.user && !result.error) {
      try {
        await userProfileService.createUserProfile(result.user, '')
      } catch (error) {
        console.error('Error creating user profile:', error)
      }
    }

    return result
  }

  const signInWithGoogle = async () => {
    return await authService.signInWithGoogle()
  }

  const signOut = async () => {
    return await authService.signOut()
  }

  const resetPassword = async (email: string) => {
    return await authService.resetPassword(email)
  }

  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await authService.handleRedirectResult()
      } catch (error) {
        console.error('Error handling redirect result:', error)
      }
    }

    checkRedirectResult()

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user)

      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult()
          setIsAdmin(tokenResult.claims.admin === true)
        } catch (error) {
          console.error('Error reading auth claims:', error)
          setIsAdmin(false)
        }
        await loadUserProfile(user)
      } else {
        setIsAdmin(false)
        setUserProfile(null)
      }

      setLoading(false)
    })

    return unsubscribe
  }, [])

  const isAuthenticated = !!currentUser
  const userEmail = currentUser?.email || ''
  const displayName = userProfile?.displayName || currentUser?.displayName || userEmail.split('@')[0] || 'Användare'
  const companyName = userProfile?.companyName || ''

  const value: AuthContextType = {
    currentUser,
    loading,
    userProfile,
    profileLoading,
    isAuthenticated,
    isAdmin,
    userEmail,
    displayName,
    companyName,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    refreshProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}